import { ContextMenu } from "@base-ui/react/context-menu";
import { type RefObject, useEffect } from "react";
import {
    getUnitCategoryConfiguration,
    isKnownUnitCategory,
} from "verdi-common/Configurations/Units/UnitCategory/UnitCategoryConfigurations";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import { UnitConfigurations } from "verdi-common/Configurations/Units/UnitConfigurations";
import { MenuPopup, MenuRadioGroup, MenuRadioItem } from "@/components/ui/menu";
import { useDataViewerStore } from "../../data-viewer-provider";
import type { AxisNameAnchor } from "./get-axis-name-anchor";

export type PanelUnitPickerState = {
    unitCategory: string;
    currentUnit: Unit | null;
    anchor: AxisNameAnchor;
};

const SCROLLABLE_OVERFLOW = new Set(["auto", "scroll", "overlay"]);

/** Walk up from `element` and collect ancestors that can scroll (overflow auto/scroll/overlay). */
function getScrollableAncestors(element: HTMLElement): HTMLElement[] {
    const ancestors: HTMLElement[] = [];
    for (let el = element.parentElement; el; el = el.parentElement) {
        const { overflow, overflowY } = getComputedStyle(el);
        if (SCROLLABLE_OVERFLOW.has(overflowY) || SCROLLABLE_OVERFLOW.has(overflow)) ancestors.push(el);
    }
    return ancestors;
}

function isOutsidePressOnAnchor(event: Event | undefined, anchor: AxisNameAnchor): boolean {
    if (!(event instanceof MouseEvent)) return false;
    const { clientX, clientY } = event;
    const { x, y, width, height } = anchor;
    return clientX >= x && clientX <= x + width && clientY >= y && clientY <= y + height;
}

export function PanelUnitPicker({
    pickerState,
    onPickerStateChange,
    scrollAnchorRef,
}: {
    pickerState: PanelUnitPickerState | null;
    onPickerStateChange: (state: PanelUnitPickerState | null) => void;
    scrollAnchorRef?: RefObject<HTMLElement | null>; // chart div from Panel — start walking up to find scroll containers
}) {
    const setUnitOverride = useDataViewerStore((state) => state.setUnitOverride);

    // When the picker is opened, the anchor is a snapshot and doesn't change when we scroll the page.
    // So, if we detect any scroll events, simply close the picker (otherwise it stays put and looks weird)
    useEffect(() => {
        if (!pickerState) return undefined;
        const anchorEl = scrollAnchorRef?.current;
        if (!anchorEl) return undefined;
        const close = () => onPickerStateChange(null);
        const scrollParents = getScrollableAncestors(anchorEl);
        scrollParents.forEach((el) => el.addEventListener("scroll", close, { passive: true }));
        return () => scrollParents.forEach((el) => el.removeEventListener("scroll", close));
    }, [pickerState, scrollAnchorRef, onPickerStateChange]);

    return (
        // ContextMenu is a deliberate choice over the more semantically correct Menu or Popover,
        // because Menu enables a dismiss-on-hover when we open it programatically, and Popover
        // uses an animation that would "fly in" from the top right of the page.
        <ContextMenu.Root
            open={pickerState !== null}
            onOpenChange={(open, eventDetails) => {
                if (open) return;
                if (
                    pickerState &&
                    eventDetails.reason === "outside-press" &&
                    isOutsidePressOnAnchor(eventDetails.event, pickerState.anchor)
                ) {
                    eventDetails.cancel();
                    return;
                }
                onPickerStateChange(null);
            }}
        >
            {pickerState && (
                <MenuPopup
                    anchor={{ getBoundingClientRect: () => DOMRect.fromRect(pickerState.anchor) }}
                    side={"bottom"}
                    align={"start"}
                    sideOffset={2}
                >
                    <MenuRadioGroup
                        value={pickerState.currentUnit}
                        onValueChange={(unit) => {
                            // The picker only opens for known categories; guard narrows string -> UnitCategory.
                            if (isKnownUnitCategory(pickerState.unitCategory)) {
                                setUnitOverride(pickerState.unitCategory, unit as Unit);
                            }
                            onPickerStateChange(null);
                        }}
                    >
                        {(getUnitCategoryConfiguration(pickerState.unitCategory)?.allowedUnits ?? []).map((unit) => (
                            <MenuRadioItem key={unit} value={unit}>
                                {UnitConfigurations[unit]?.symbol ?? unit}
                            </MenuRadioItem>
                        ))}
                    </MenuRadioGroup>
                </MenuPopup>
            )}
        </ContextMenu.Root>
    );
}
